import { useCallback } from "react";
import { useFetch } from "../useFetch";
import type { 
  SessionResponse, 
  LoginPayload, 
  ResetPasswordPayload,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  ResendVerificationEmailPayload,
  ResendVerificationEmailResponse,
  VerifyEmailResponse,
} from "../../core/types/control";

// ============================================
// LOGIN
// ============================================
export function useLogin() {
  // <Respuesta, Payload>
  const fetch = useFetch<SessionResponse, LoginPayload>({
    endpoint: "/session/login",
    method: "POST",
    auto: false,
  });

  const login = (credentials: LoginPayload) => {
    return fetch.execute({ body: credentials });
  };

  return { ...fetch, login };
}

// ============================================
// LOGOUT
// ============================================
export function useLogout() {
  const fetch = useFetch<{ message: string }>({
    endpoint: "/session/logout",
    method: "POST",
    auto: false,
  });

  const logout = () => {
    return fetch.execute();
  };

  return { ...fetch, logout };
}

// ============================================
// RESET PASSWORD
// ============================================
export function useResetPassword() {
  const fetch = useFetch<{ message: string }, ResetPasswordPayload>({
    endpoint: "/verify/reset-password",
    method: "POST",
    auto: false,
  });

  const resetPassword = (email: string) => {
    return fetch.execute({ body: { email } });
  };

  return { ...fetch, resetPassword };
}

// ============================================
// FORGOT PASSWORD
// ============================================
export function useForgotPassword() {
  const fetch = useFetch<ForgotPasswordResponse, ForgotPasswordPayload>({
    endpoint: "/verify/forgot-password",
    method: "POST",
    auto: false,
  });

  const forgotPassword = (email: string) => {
    return fetch.execute({ body: { email } });
  };

  return { ...fetch, forgotPassword };
}

// ============================================
// RESEND VERIFICATION EMAIL
// ============================================
export function useResendVerificationEmail() {
  const fetch = useFetch<
    ResendVerificationEmailResponse,
    ResendVerificationEmailPayload
  >({
    endpoint: "/verify/resend",
    method: "POST",
    auto: false,
  });

  const resendVerificationEmail = (email: string) => {
    return fetch.execute({ body: { email } });
  };

  return { ...fetch, resendVerificationEmail };
}

// ============================================
// VERIFY EMAIL
// ============================================
export function useVerifyEmail() {
  const fetch = useFetch<VerifyEmailResponse>({
    endpoint: "/verify",
    method: "GET",
    auto: false,
  });

  const verifyEmail = useCallback(
    (token: string, email: string) => {
      return fetch.execute({
        query: { token, email },
      });
    },
    // fetch.execute es estable (useCallback con deps primitivas en useFetch).
    // Depender de `fetch` (el objeto entero) causaría un bucle infinito porque
    // useFetch devuelve un objeto nuevo en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetch.execute],
  );

  return { ...fetch, verifyEmail };
}

// ============================================
// VERIFY SESSION
// ============================================
export function useVerifySession() {
  return useFetch<{
    isAuthenticated: boolean;
    userId?: number;
    role?: string | null;
    message?: string;
  }>({
    endpoint: "/session/verify",
    method: "GET",
  });
}